import type { ChangeEvent, KeyboardEvent } from "react";

import { cn } from "@/lib/utils";

import {
  COMPOSER_CONTINUE,
  COMPOSER_LABEL_MULTI,
  COMPOSER_LABEL_SR,
  COMPOSER_PLACEHOLDER,
  COMPOSER_SEND,
} from "../copy";
import { canSendAnswer, COUNTER_FROM, MAX_ANSWER_CHARS } from "../state";
import { HwButton } from "./hw-button";

interface ComposerProps {
  /** True on the multi-select question: the label and the button both change. */
  multi: boolean;
  text: string;
  onTextChange: (value: string) => void;
  onSend: () => void;
  /** True while an answer is being scored: blocks a double send. */
  busy: boolean;
  /** At least one chip is selected. */
  hasChips: boolean;
}

const TEXTAREA_ID = "hw-answer";

/** The answer box pinned under the chat. */
export function Composer({ multi, text, onTextChange, onSend, busy, hasChips }: ComposerProps) {
  // Free text never answers a question on its own: a chip carries the score, so
  // a single-select question stays unsendable until one is picked. The
  // multi-select question accepts an empty selection as its own answer.
  const canSend = !busy && canSendAnswer(multi, hasChips);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    // Pasting a long answer truncates rather than rejects (SPEC section 6).
    onTextChange(event.target.value.slice(0, MAX_ANSWER_CHARS));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && canSend) {
      event.preventDefault();
      onSend();
    }
  }

  return (
    <div>
      {/* Single-select questions need no visible prompt, but the textarea
          still needs an accessible name. */}
      <label htmlFor={TEXTAREA_ID} className={multi ? "hw-mono block" : "sr-only"}>
        {multi ? COMPOSER_LABEL_MULTI : COMPOSER_LABEL_SR}
      </label>

      <textarea
        id={TEXTAREA_ID}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        maxLength={MAX_ANSWER_CHARS}
        rows={2}
        placeholder={COMPOSER_PLACEHOLDER}
        disabled={busy}
        className={cn(
          multi && "mt-2",
          "border-hw-line bg-hw-card text-hw-ink placeholder:text-hw-mute focus:border-hw-blue focus-visible:outline-hw-blue hw-body w-full resize-none rounded-[4px] border px-4 py-3 transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2",
        )}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {text.length >= COUNTER_FROM && (
            <span className="text-hw-mute text-[12px]">
              {text.length} / {MAX_ANSWER_CHARS}
            </span>
          )}
        </div>

        <HwButton onClick={onSend} disabled={!canSend}>
          {multi ? COMPOSER_CONTINUE : COMPOSER_SEND}
        </HwButton>
      </div>
    </div>
  );
}
